import { ChevronDown, Fuel, ShieldCheck, Truck, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useProfile, type ProfileKey } from '@/contexts/ProfileContext';

// Switcher de perfil. Renderiza no TOPO da sidebar (acima do logo).
//   - User com 0 perfis: nao renderiza (estado anomalo).
//   - User com 1 perfil: chip estatico (sem dropdown).
//   - User com >1 perfil: dropdown listando perfis disponiveis.
//
// Aceita prop isExpanded pra adaptar layout (sidebar fixada/recolhida).
// Em modo colapsado mostra apenas icone centralizado com tooltip.

interface ProfileMeta {
  key: ProfileKey;
  label: string;
  shortLabel: string;
  hint: string;
  icon: LucideIcon;
}

const PROFILE_META: Record<ProfileKey, ProfileMeta> = {
  frotista: {
    key: 'frotista',
    label: 'Frotista',
    shortLabel: 'Frotista',
    hint: 'Você está como cliente',
    icon: Truck,
  },
  posto: {
    key: 'posto',
    label: 'Posto de Combustível',
    shortLabel: 'Posto',
    hint: 'Você está como rede credenciada',
    icon: Fuel,
  },
  admin: {
    key: 'admin',
    label: 'Admin',
    shortLabel: 'Admin',
    hint: 'Modo administrador da plataforma',
    icon: ShieldCheck,
  },
};

interface ProfileSwitcherProps {
  isExpanded: boolean;
}

export function ProfileSwitcher({ isExpanded }: ProfileSwitcherProps) {
  const { availableProfiles, activeProfile, setActiveProfile, canSwitch } = useProfile();

  if (!activeProfile) return null; // user sem nenhum vinculo

  const meta = PROFILE_META[activeProfile];
  const Icon = meta.icon;

  function handleSelect(next: ProfileKey) {
    if (next === activeProfile) return;
    const nextMeta = PROFILE_META[next];
    setActiveProfile(next);
    toast.success(`Perfil alterado para ${nextMeta.label}`);
  }

  // Caso 1: user com 1 perfil so. Chip estatico (sem dropdown).
  if (!canSwitch) {
    return (
      <div
        data-testid="profile-switcher-static"
        title={!isExpanded ? meta.label : undefined}
        className={cn(
          'flex items-center rounded-md',
          isExpanded ? 'gap-2.5 px-3 py-2' : 'justify-center px-0 py-2',
        )}
        style={{ background: 'rgba(255, 255, 255, 0.04)' }}
      >
        <Icon className="w-[18px] h-[18px] flex-shrink-0 text-white" aria-hidden="true" />
        {isExpanded ? (
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-white truncate leading-tight">
              {meta.label}
            </p>
            <p
              className="text-[10.5px] truncate leading-tight"
              style={{ color: 'var(--color-sidebar-text-mute)' }}
            >
              {meta.hint}
            </p>
          </div>
        ) : null}
      </div>
    );
  }

  // Caso 2: user com >1 perfil. Dropdown.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="profile-switcher-trigger"
          aria-label={`Perfil ativo: ${meta.label}. Clique para trocar.`}
          title={!isExpanded ? `${meta.label} (clique para trocar)` : undefined}
          className={cn(
            'group flex items-center w-full rounded-md transition',
            'hover:bg-white/[0.06]',
            isExpanded ? 'gap-2.5 px-3 py-2' : 'justify-center px-0 py-2',
          )}
          style={{ background: 'rgba(255, 255, 255, 0.04)' }}
        >
          <Icon className="w-[18px] h-[18px] flex-shrink-0 text-white" aria-hidden="true" />
          {isExpanded ? (
            <>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[13px] font-semibold text-white truncate leading-tight">
                  {meta.label}
                </p>
                <p
                  className="text-[10.5px] truncate leading-tight"
                  style={{ color: 'var(--color-sidebar-text-mute)' }}
                >
                  {meta.hint}
                </p>
              </div>
              <ChevronDown
                className="w-4 h-4 flex-shrink-0 transition group-data-[state=open]:rotate-180"
                style={{ color: 'var(--color-sidebar-text-mute)' }}
                aria-hidden="true"
              />
            </>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="min-w-[240px]"
        data-testid="profile-switcher-menu"
      >
        <DropdownMenuLabel>Trocar perfil</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableProfiles.map((key) => {
          const item = PROFILE_META[key];
          const ItemIcon = item.icon;
          const isActive = key === activeProfile;
          return (
            <DropdownMenuItem
              key={key}
              data-testid={`profile-switcher-option-${key}`}
              onSelect={() => handleSelect(key)}
              className={cn('gap-2.5', isActive && 'bg-primary-soft text-primary')}
            >
              <ItemIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-tight">{item.label}</p>
                <p className="text-[11px] text-text-muted truncate leading-tight">
                  {item.hint}
                </p>
              </div>
              {isActive ? (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                  Atual
                </span>
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
